import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Booking.css';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface BookingForm {
  name: string;
  email: string;
  phone: string;
  petName: string;
  petType: 'dog' | 'cat' | 'other';
  startDate: Date | null;
  endDate: Date | null;
  notes: string;
}

const Booking: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState<Value>(null);
  const [formData, setFormData] = useState<BookingForm>({
    name: '',
    email: '',
    phone: '',
    petName: '',
    petType: 'dog',
    startDate: null,
    endDate: null,
    notes: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Example unavailable dates (you would fetch these from a backend)
  const unavailableDates = [
    new Date(2025, 11, 24), // Dec 24
    new Date(2025, 11, 25), // Dec 25
    new Date(2025, 11, 31), // Dec 31
    new Date(2026, 0, 1),   // Jan 1
  ];

  const isDateUnavailable = (date: Date) => {
    return unavailableDates.some(
      unavailable => 
        date.getFullYear() === unavailable.getFullYear() &&
        date.getMonth() === unavailable.getMonth() &&
        date.getDate() === unavailable.getDate()
    );
  };

  const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      // Disable past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) return true;
      
      // Disable unavailable dates
      return isDateUnavailable(date);
    }
    return false;
  };

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      if (isDateUnavailable(date)) {
        return 'unavailable-date';
      }
    }
    return '';
  };

  const handleDateChange = (value: Value) => {
    setSelectedRange(value);
    if (Array.isArray(value)) {
      setFormData({
        ...formData,
        startDate: value[0],
        endDate: value[1]
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    // EmailJS configuration - REPLACE THESE WITH YOUR ACTUAL VALUES
    const serviceId = 'YOUR_SERVICE_ID';
    const templateId = 'YOUR_TEMPLATE_ID';
    const publicKey = 'YOUR_PUBLIC_KEY';

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      pet_name: formData.petName,
      pet_type: formData.petType,
      start_date: formatDate(formData.startDate),
      end_date: formatDate(formData.endDate),
      nights: calculateNights(),
      notes: formData.notes || 'No additional notes provided'
    };

    try {
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to send email:', error);
      setErrorMessage('Failed to send booking request. Please try again or contact us directly at thepetnannyofweston@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateNights = () => {
    if (formData.startDate && formData.endDate) {
      const diffTime = Math.abs(formData.endDate.getTime() - formData.startDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 0;
  };

  if (isSubmitted) {
    return (
      <div className="booking-page">
        <section className="booking-hero">
          <h1>Book Your Pet's Stay</h1>
          <p>Request a reservation for your furry friend</p>
        </section>
        <section className="success-section">
          <div className="container">
            <div className="success-message">
              <span className="success-icon">✅</span>
              <h2>Booking Request Received!</h2>
              <p>Thank you for your interest in The Pet Nanny of Weston!</p>
              <div className="booking-summary">
                <h3>Your Request Summary:</h3>
                <p><strong>Pet:</strong> {formData.petName} ({formData.petType})</p>
                <p><strong>Dates:</strong> {formatDate(formData.startDate)} - {formatDate(formData.endDate)}</p>
                <p><strong>Nights:</strong> {calculateNights()}</p>
              </div>
              <p className="next-steps">
                Suzanne will review your request and contact you within 24 hours to confirm availability 
                and discuss your pet's specific needs.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    petName: '',
                    petType: 'dog',
                    startDate: null,
                    endDate: null,
                    notes: ''
                  });
                  setSelectedRange(null);
                }}
              >
                Make Another Request
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <section className="booking-hero">
        <h1>Book Your Pet's Stay</h1>
        <p>Request a reservation for your furry friend</p>
      </section>

      <section className="booking-content">
        <div className="container">
          <div className="booking-layout">
            <div className="calendar-section">
              <h2>📅 Select Your Dates</h2>
              <p className="calendar-instructions">
                Click and drag to select a date range, or click a start date and then an end date.
              </p>
              <div className="calendar-wrapper">
                <Calendar
                  onChange={handleDateChange}
                  value={selectedRange}
                  selectRange={true}
                  tileDisabled={tileDisabled}
                  tileClassName={tileClassName}
                  minDate={new Date()}
                  locale="en-US"
                />
              </div>
              <div className="calendar-legend">
                <div className="legend-item">
                  <span className="legend-dot available"></span>
                  <span>Available</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot selected"></span>
                  <span>Selected</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot unavailable"></span>
                  <span>Unavailable</span>
                </div>
              </div>
              {formData.startDate && formData.endDate && (
                <div className="selected-dates">
                  <h3>Your Selected Dates</h3>
                  <p><strong>Check-in:</strong> {formatDate(formData.startDate)}</p>
                  <p><strong>Check-out:</strong> {formatDate(formData.endDate)}</p>
                  <p className="nights-count">{calculateNights()} night{calculateNights() !== 1 ? 's' : ''}</p>
                </div>
              )}
            </div>

            <div className="form-section">
              <h2>📝 Your Information</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="John Smith"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="(954) 555-1234"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="petName">Pet's Name *</label>
                    <input
                      type="text"
                      id="petName"
                      name="petName"
                      value={formData.petName}
                      onChange={handleInputChange}
                      required
                      placeholder="Buddy"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="petType">Pet Type *</label>
                    <select
                      id="petType"
                      name="petType"
                      value={formData.petType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="dog">🐕 Dog</option>
                      <option value="cat">🐈 Cat</option>
                      <option value="other">🐾 Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Additional Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Tell us about your pet, any special needs, multiple pets, etc."
                  />
                </div>

                {errorMessage && (
                  <div className="error-message" style={{
                    background: '#fee',
                    border: '1px solid #fcc',
                    padding: '1rem',
                    borderRadius: '8px',
                    color: '#c33',
                    marginBottom: '1rem'
                  }}>
                    {errorMessage}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={!formData.startDate || !formData.endDate || isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Request Booking'}
                </button>

                <p className="form-note">
                  * This is a booking request. Suzi will contact you to confirm availability and finalize details.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="booking-info">
        <div className="container">
          <h2>Booking Information</h2>
          <div className="info-cards">
            <div className="info-card">
              <span className="info-icon">🕐</span>
              <h3>Check-in & Check-out</h3>
              <p>Drop-off & pick-up available<br/>8am - 8pm, 7 days a week<br/>Please schedule in advance</p>
            </div>
            <div className="info-card">
              <span className="info-icon">📅</span>
              <h3>Holiday Bookings</h3>
              <p>Holiday periods book quickly! Reserve at least 2-4 weeks in advance for holiday dates.</p>
            </div>
            <div className="info-card">
              <span className="info-icon">📞</span>
              <h3>Questions?</h3>
              <p>Contact us at<br/>thepetnannyofweston@gmail.com<br/>We're happy to help!</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
